import { useEffect, useState, useRef } from "react";
import {
  Box,
  Heading,
  useToast,
  Button,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
  Center,
  VStack,
  Text,
  Stack,
  SimpleGrid,
  Select,
  Divider,
  HStack,
  Flex,
} from "@chakra-ui/react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import SmallMapWidget from "./SmallMapWidget"; // Added map widget import

interface ILocation {
  latitude: number;
  longitude: number;
  altitude: number | undefined | null;
  accuracy: number;
  altitudeAccuracy: number | undefined | null;
  heading: number | undefined | null;
  speed: number | undefined | null;
}

// Add FlowSensorSize enum and options for flow sensor select
enum FlowSensorSize {
  NO_FLOW_SENSOR = 0,
  THREE_QUARTER = 1, // represents 3/4"
  ONE = 2, // represents 1"
  ONE_AND_HALF = 3, // represents 1.5"
  TWO = 4, // represents 2"
}
const flowSensorSizeOptions = [
  { value: FlowSensorSize.NO_FLOW_SENSOR, label: "No flow sensor" },
  { value: FlowSensorSize.THREE_QUARTER, label: "3/4'" },
  { value: FlowSensorSize.ONE, label: "1'" },
  { value: FlowSensorSize.ONE_AND_HALF, label: "1.5'" },
  { value: FlowSensorSize.TWO, label: "2'" },
];

const EditDeviceView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [deviceName, setDeviceName] = useState("");
  const [deviceAddress, setDeviceAddress] = useState("");
  const [location, setLocation] = useState<ILocation>();
  const [SensorDepth, setSensorDepth] = useState<number | undefined>(undefined);
  const [flowSensorSize, setFlowSensorSize] = useState<number>(FlowSensorSize.NO_FLOW_SENSOR);
  const [locationIsLoading, setLocationIsLoading] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualLatitude, setManualLatitude] = useState("");
  const [manualLongitude, setManualLongitude] = useState("");
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [deviceType, setDeviceType] = useState<string>("");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const toast = useToast();
  const api = useAxios();

  const check = () => {
    console.log("data" + location?.latitude, location?.longitude);
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
      setLocationIsLoading(true);
    } else {
      console.log("Geolocation not supported");
      toast({
        title: <Trans>Geolocation not supported</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }

    function success(position: GeolocationPosition) {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;
      const altitude = position.coords.altitude;
      const accuracy = position.coords.accuracy;
      const altitudeAccuracy = position.coords.altitudeAccuracy;
      const heading = position.coords.heading;
      const speed = position.coords.speed;

      setLocation({
        latitude: latitude,
        longitude: longitude,
        altitude: altitude,
        accuracy: accuracy,
        altitudeAccuracy: altitudeAccuracy,
        heading: heading,
        speed: speed,
      });
      setLocationIsLoading(false);
    }

    function error() {
      console.log("Unable to retrieve your location");
      toast({
        title: <Trans>Unable to retrieve your location</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      setLocationIsLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    let lat = location?.latitude;
    let long = location?.longitude;
    let accuracy = location?.accuracy;

    if (showManualEntry) {
      if (manualLatitude === "" || manualLongitude === "") {
        toast({
          title: <Trans>Please enter both latitude and longitude</Trans>,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }
      lat = parseFloat(manualLatitude);
      long = parseFloat(manualLongitude);
      accuracy = 0;
    }

    try {
      const response = await api.post(`/frontend/devices/custom-associate/${id}/`, {
        device_address: deviceAddress,
        device_name: deviceName,
        latitude: lat,
        longitude: long,
        accuracy: accuracy,
        altitude: location?.altitude,
        altitude_accuracy: location?.altitudeAccuracy,
        sensor_depth: SensorDepth,
        flow_sensor_size: flowSensorSize,
      });
      // If the response is 201, the device was added successfully
      // If the response is 200, the device was modified successfully
      if (response.status === 201) {
        console.log("Device Added Successfully");
        toast({
          title: <Trans>Device Added Successfully</Trans>,
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      }
      if (response.status === 200) {
        console.log("Device Modified Successfully");
        toast({
          title: <Trans>Device Modified Successfully</Trans>,
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      }
      navigate(-1);
    } catch (error) {
      console.error("Error updating device:", error);
      const errorMessage =
        error instanceof Error ? error.message : <Trans>An unexpected error occurred</Trans>;
      const apiErrorMessage = (error as any)?.response?.data?.message;
      toast({
        title: <Trans>Error updating device</Trans>,
        description: apiErrorMessage || errorMessage,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const fetchData = async () => {
    try {
      const response = await api.get("frontend/user-devices/" + id + "/");
      console.log(response.data);
      setDeviceName(response.data.device_name);
      setDeviceAddress(response.data.device_address);
      setSensorDepth(response.data.sensor_depth);
      setDeviceType(response.data.device_type);
      console.log("Device Type:", response.data.device_type);

      if (response.data.latitude && response.data.longitude) {
        setLocation({
          latitude: response.data.latitude,
          longitude: response.data.longitude,
          accuracy: response.data.accuracy,
          altitude: response.data.altitude,
          altitudeAccuracy: response.data.altitude_accuracy,
          heading: response.data.heading,
          speed: response.data.speed,
        });
      }
    } catch {
      // Toast
      toast({
        title: <Trans>Error fetching data</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleDeleteDevice = () => {
    onOpen();
  };

  const confirmDelete = () => {
    api
      .delete("frontend/user-devices/" + id + "/")
      .then(() => {
        toast({
          title: <Trans>Device deleted</Trans>,
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          title: <Trans>Error deleting device</Trans>,
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      });
    onClose();

    // Redirect to front page
    navigate("/");
  };

  return (
    <>
      <Navbar>
        <Center py={8}>
          <Box p={6} bg="white" boxShadow="lg" borderRadius="md" width={{ base: "90%", md: 500 }}>
            <VStack spacing={4} align="stretch">
              <Heading size="lg" textAlign="center">
                <Trans>Edit Device</Trans>
              </Heading>
              <form onSubmit={handleSubmit}>
                <FormControl id="deviceAddress" isRequired>
                  <FormControl id="deviceName" isRequired={false}>
                    <FormLabel mt="2">
                      <Trans>Device Name</Trans>
                    </FormLabel>
                    <Input
                      type="text"
                      value={deviceName}
                      isRequired={false}
                      onChange={(e) => setDeviceName(e.target.value)}
                      placeholder={t`e.g. "My Device"`}
                    />
                  </FormControl>

                  {deviceType === "SENSOR" && (
                    <FormControl>
                      <FormLabel>
                        <Trans>Sensor Depth (cm)</Trans>
                      </FormLabel>
                      <Select
                        placeholder={t`Fully submerged`}
                        value={SensorDepth?.toString() ?? ""}
                        onChange={(e) => setSensorDepth(+e.target.value)}
                      >
                        {[5, 10, 15].map((d) => (
                          <option key={d} value={d}>
                            {d}cm over ground
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {deviceType === "VALVE_WITH_FLOW_SENSOR" && (
                    <FormControl isRequired>
                      <FormLabel>
                        <Trans>Flow sensor</Trans>
                      </FormLabel>
                      <Select
                        placeholder={t`No flow sensor`}
                        value={flowSensorSize?.toString() ?? ""}
                        onChange={(e) => setFlowSensorSize(+e.target.value)}
                      >
                        {flowSensorSizeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </FormControl>

                <Divider p={2} />
                <HStack spacing={4} align="center" marginTop={4}>
                  <Button
                    colorScheme="teal"
                    onClick={getLocation}
                    variant={"outline"}
                    isLoading={locationIsLoading}
                    disabled={showManualEntry}
                  >
                    <Trans>Register GPS location</Trans>
                  </Button>
                  <Divider orientation="vertical" height="24px" />
                  <Button colorScheme="teal" variant={"outline"} onClick={() => setIsMapOpen(true)}>
                    <Trans>Pick on map</Trans>
                  </Button>
                </HStack>

                <Text textAlign="left" marginTop={4}>
                  <Trans>or</Trans>
                </Text>

                <Button
                  mt={4}
                  width="full"
                  colorScheme="teal"
                  variant={"outline"}
                  onClick={() => setShowManualEntry(!showManualEntry)}
                >
                  <Trans>Change coordinates manually</Trans>
                </Button>
                {showManualEntry && (
                  <>
                    <FormControl id="manualLatitude" isRequired={showManualEntry}>
                      <FormLabel>
                        <Trans>Manual Latitude</Trans>
                      </FormLabel>
                      <Input
                        type="number"
                        step="any"
                        value={manualLatitude}
                        onChange={(e) => setManualLatitude(e.target.value)}
                        placeholder={t`E.g. 51.507422`}
                      />
                    </FormControl>
                    <FormControl id="manualLongitude" isRequired={showManualEntry}>
                      <FormLabel>
                        <Trans>Manual Longitude</Trans>
                      </FormLabel>
                      <Input
                        type="number"
                        step="any"
                        value={manualLongitude}
                        onChange={(e) => setManualLongitude(e.target.value)}
                        placeholder={t`E.g. 12.127887`}
                      />
                    </FormControl>
                  </>
                )}

                <Text textAlign="left" marginTop={4}>
                  Current location:
                </Text>
                {location && !showManualEntry ? (
                  <HStack spacing={3} p={2}>
                    <Text>
                      <Trans>Latitude:</Trans> {location.latitude}
                    </Text>
                    <Text>
                      <Trans>Longitude:</Trans> {location.longitude}
                    </Text>
                    <Text>
                      <Trans>Accuracy:</Trans> {location.accuracy.toFixed(0)}m
                    </Text>
                  </HStack>
                ) : null}
                <HStack spacing={4} align="center" marginTop={4}>
                  <Button flex={1} size="md" colorScheme="teal" type="submit">
                    <Trans>Save Changes</Trans>
                  </Button>
                  <Button
                    flex={1}
                    size="md"
                    onClick={() => handleDeleteDevice()}
                    variant="outline"
                    colorScheme="red"
                  >
                    <Trans>Delete</Trans>
                  </Button>
                </HStack>
              </form>
            </VStack>
          </Box>
        </Center>
      </Navbar>
      <SmallMapWidget
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        onSelect={(lat, lng) => {
          setShowManualEntry(false);
          setLocation({
            latitude: lat,
            longitude: lng,
            altitude: null,
            accuracy: 0,
            altitudeAccuracy: null,
            heading: null,
            speed: null,
          });
          check();
          setTimeout(() => setIsMapOpen(false), 0);
          setIsMapOpen(false);
        }}
      />
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <Trans>Delete User Device</Trans>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Trans>Are you sure? You can't undo this action afterwards.</Trans>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                <Trans>Delete</Trans>
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default EditDeviceView;
